import { FIRST_CANONICAL_PARAM_REGEX } from '@doda/common/src/constants/regex';
import {
  CARD_NAV_SUBSEQUENT_CANONICAL_PARAM_REGEX,
  COND_QUERIES,
} from 'components/CardNavigationPanel/constants';

const EXCLUDE_CONDITION_KEYS = ['pic', 'ds', 'so', 'pf', 'tp'];

/**
 * 対象の条件パラメータを抽出
 * @returns EXCLUDE_CONDITION_KEYSを除いた対象パラメータ
 */
export const getTargetConditionQueries = () =>
  COND_QUERIES.filter((query) => !EXCLUDE_CONDITION_KEYS.includes(query));

/**
 * カノニカル形式のURLから特定のパラメータキーを抽出する関数
 * @param {Object} params
 * @param {RegExp} params.regex - パラメータキーを抽出するための正規表現
 * @param {string} params.url - 解析するURL
 * @param {string[]} params.targetQueries - 抽出対象とするクエリパラメータのキーのリスト
 * @param {string[]} params.paramKeys - 抽出されたパラメータキーを格納するための配列
 * @returns {void}
 */
const extractCanonicalParams = ({
  regex,
  url,
  targetQueries,
  paramKeys,
}: {
  regex: RegExp;
  url: string;
  targetQueries: string[];
  paramKeys: string[];
}) => {
  const globalRegex = new RegExp(regex, 'g');
  // 与えられた正規表現にマッチする部分を全て抽出
  const matches = url.match(globalRegex) || [];
  matches.forEach((match) => {
    // マッチした文字列からパラメータキーを抽出（'j_'または'-'を削除）
    const key = match.split('__')[0].replace(/\/j_|\/-/, '');
    // 対象のクエリパラメータに含まれていて、まだリストになければ追加
    if (targetQueries.includes(key) && !paramKeys.includes(key)) {
      paramKeys.push(key);
    }
  });
};

/**
 * クエリ形式のURLから特定のパラメータキーを抽出する関数
 * @param {Object} params
 * @param {string} params.url - 解析するURL
 * @param {string[]} params.targetQueries - 抽出対象とするクエリパラメータのキーのリスト
 * @param {string[]} params.paramKeys - 抽出されたパラメータキーを格納するための配列
 * @returns {void}
 */
const extractQueryParams = ({
  url,
  targetQueries,
  paramKeys,
}: {
  url: string;
  targetQueries: string[];
  paramKeys: string[];
}) => {
  const queryParams = new URLSearchParams(url.split('?')[1] || '');
  queryParams.forEach((_, key) => {
    if (targetQueries.includes(key) && !paramKeys.includes(key)) {
      paramKeys.push(key);
    }
  });
};

/**
 * URLから対象のパラメータを抽出する
 * @param url URL
 * @param targetQueries 抽出対象パラメータ
 * @returns 抽出したパラメータキーのリスト
 */
export const extractParamKeys = (url: string, targetQueries: string[]) => {
  // 抽出したパラメータキーを保持する配列
  const paramKeys: string[] = [];

  // 最初とそれ以降のカノニカルパラメータに対応する正規表現を使って抽出
  extractCanonicalParams({
    regex: FIRST_CANONICAL_PARAM_REGEX,
    url,
    targetQueries,
    paramKeys,
  });
  extractCanonicalParams({
    regex: CARD_NAV_SUBSEQUENT_CANONICAL_PARAM_REGEX,
    url,
    targetQueries,
    paramKeys,
  });

  // 通常のクエリパラメータからキーを抽出
  extractQueryParams({ url, targetQueries, paramKeys });

  // 抽出したパラメータキーのリストを返す
  return paramKeys;
};

/**
 * 二つのURLが同じ対象の条件パラメータを持っているかどうかを判断する関数
 * @param url1 URL1
 * @param url2 URL2
 * @returns 同じ対象の条件パラメータを持っているかどうかの判定結果
 */
export const judgeSameParams = (url1: string, url2: string) => {
  // 対象の条件パラメータ
  const targetQueries = getTargetConditionQueries();

  // それぞれのURLから対象の条件パラメータキーを抽出
  const keys1 = extractParamKeys(url1, targetQueries);
  const keys2 = extractParamKeys(url2, targetQueries);

  // 抽出したキーの数が異なれば、同じパラメータを持たないと判断
  if (keys1.length !== keys2.length) return false;

  // 両方のURLで抽出したキーが相互に含まれているかどうかを判断
  return (
    keys1.every((key) => keys2.includes(key)) &&
    keys2.every((key) => keys1.includes(key))
  );
};
